import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, Clock, TrendingUp, UserCheck, UserX, Download, GraduationCap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/utils/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#DC143C', '#10B981', '#F59E0B', '#6B7280'];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [trainingStats, setTrainingStats] = useState(null);
  const [trainingCharts, setTrainingCharts] = useState(null);

  useEffect(() => {
    loadStats();
    loadTraining();
  }, []);

  const loadTraining = async () => {
    try {
      const [s, c] = await Promise.all([api.getTrainingStats(), api.getTrainingCharts()]);
      setTrainingStats(s.data);
      setTrainingCharts(c.data);
    } catch (e) { /* silencio si no hay datos */ }
  };

  const loadStats = async (customStartDate = null, customEndDate = null) => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API}/stats/dashboard`;
      
      const start = customStartDate !== null ? customStartDate : startDate;
      const end = customEndDate !== null ? customEndDate : endDate;
      
      // Add date parameters if both are set
      if (start && end) {
        url += `?start_date=${start}&end_date=${end}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
      toast.error('Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = () => {
    if (startDate && endDate) {
      if (startDate > endDate) {
        toast.error('La fecha de inicio debe ser anterior a la fecha final');
        return;
      }
      loadStats();
    } else if (startDate || endDate) {
      toast.error('Debe seleccionar ambas fechas (inicio y fin)');
    }
  };

  const handleClearFilter = () => {
    setStartDate('');
    setEndDate('');
    loadStats('', '');
  };

  const downloadPDF = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/reports/visits-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reporte_visitas_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Reporte descargado exitosamente');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Error al descargar el reporte');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">Cargando estadísticas...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!stats) return null;

  // Prepare data for charts
  const statusData = Object.entries(stats.status_breakdown).map(([key, value]) => ({
    name: key === 'pending_entry' ? 'Pendiente' : key === 'approved_entry' ? 'Dentro' : 'Completado',
    value
  }));

  const departmentData = Object.entries(stats.department_breakdown).map(([name, count]) => ({
    name: name.length > 15 ? name.substring(0, 15) + '...' : name,
    count
  }));

  const topEmployeesData = stats.top_employees.map(([name, count]) => ({
    name: name.length > 20 ? name.substring(0, 20) + '...' : name,
    count
  }));

  return (
    <DashboardLayout>
      <div className="max-w-7xl" data-testid="dashboard-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 uppercase tracking-tight">Dashboard</h1>
            <p className="text-gray-600 mt-1">Estadísticas y reportes de visitas</p>
          </div>
          <Button
            onClick={downloadPDF}
            data-testid="download-pdf-button"
            className="bg-[#DC143C] hover:bg-[#B91C1C] text-white font-medium uppercase tracking-wide flex items-center gap-2"
          >
            <Download className="h-5 w-5" />
            Descargar Reporte PDF
          </Button>
        </div>

        {/* Date Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 flex-1">
              <Label className="text-sm font-medium text-gray-700 uppercase tracking-wide whitespace-nowrap">Filtrar por fecha:</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="h-10 w-40"
                placeholder="Fecha inicio"
              />
              <span className="text-gray-500">a</span>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="h-10 w-40"
                placeholder="Fecha fin"
              />
              <Button
                onClick={handleFilterChange}
                className="bg-[#DC143C] hover:bg-[#B91C1C] text-white font-medium uppercase tracking-wide"
                data-testid="apply-filter-button"
              >
                Aplicar
              </Button>
              {(startDate || endDate) && (
                <Button
                  onClick={handleClearFilter}
                  variant="outline"
                  className="font-medium uppercase tracking-wide"
                  data-testid="clear-filter-button"
                >
                  Limpiar
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium uppercase tracking-wide">Hoy</CardTitle>
              <Users className="h-5 w-5 text-[#DC143C]" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.today_visits}</div>
              <p className="text-xs text-muted-foreground mt-1">Visitas registradas</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium uppercase tracking-wide">Esta Semana</CardTitle>
              <TrendingUp className="h-5 w-5 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.week_visits}</div>
              <p className="text-xs text-muted-foreground mt-1">Últimos 7 días</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium uppercase tracking-wide">Dentro</CardTitle>
              <UserCheck className="h-5 w-5 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.currently_inside}</div>
              <p className="text-xs text-muted-foreground mt-1">Visitantes actuales</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium uppercase tracking-wide">Pendientes</CardTitle>
              <UserX className="h-5 w-5 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.pending_entry}</div>
              <p className="text-xs text-muted-foreground mt-1">Esperando aprobación</p>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Daily Trend */}
          <Card>
            <CardHeader>
              <CardTitle className="uppercase tracking-tight">Tendencia de Visitas (7 días)</CardTitle>
              <CardDescription>Número de visitas por día</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={stats.daily_trend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="count" name="Visitas" stroke="#DC143C" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Status Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="uppercase tracking-tight">Estado de Visitas</CardTitle>
              <CardDescription>Distribución por estado</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Department Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="uppercase tracking-tight">Visitas por Departamento</CardTitle>
              <CardDescription>Total de visitas por área</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={departmentData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" name="Visitas" fill="#DC143C" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Top Employees */}
          <Card>
            <CardHeader>
              <CardTitle className="uppercase tracking-tight">Empleados Más Visitados</CardTitle>
              <CardDescription>Top 5 empleados con más visitas</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={topEmployeesData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={150} />
                  <Tooltip />
                  <Bar dataKey="count" name="Visitas" fill="#10B981" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Additional Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium uppercase tracking-wide">Total de Visitas</CardTitle>
              <Users className="h-5 w-5 text-gray-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.total_visits}</div>
              <p className="text-xs text-muted-foreground mt-1">Histórico completo</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium uppercase tracking-wide">Tiempo Promedio</CardTitle>
              <Clock className="h-5 w-5 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.avg_stay_minutes} min</div>
              <p className="text-xs text-muted-foreground mt-1">Duración de visitas completadas</p>
            </CardContent>
          </Card>
        </div>

        {/* ==================== CAPACITACIÓN — Resumen ==================== */}
        {trainingStats && trainingStats.total_enrollments > 0 && (
          <div className="mt-10" data-testid="dashboard-training-section">
            <div className="flex items-center gap-3 mb-4">
              <GraduationCap className="h-6 w-6 text-[#DC143C]" />
              <h2 className="text-2xl font-bold text-gray-900 uppercase tracking-tight">Capacitación</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wide">Cursos</CardTitle></CardHeader>
                <CardContent><div className="text-2xl font-bold">{trainingStats.total_courses}</div></CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wide">Inscritos</CardTitle></CardHeader>
                <CardContent><div className="text-2xl font-bold">{trainingStats.total_enrollments}</div></CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wide">Completados</CardTitle></CardHeader>
                <CardContent><div className="text-2xl font-bold text-green-600">{trainingStats.completed}</div></CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wide">En curso</CardTitle></CardHeader>
                <CardContent><div className="text-2xl font-bold text-amber-600">{trainingStats.in_progress}</div></CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wide">Promedio</CardTitle></CardHeader>
                <CardContent><div className="text-2xl font-bold text-[#DC143C]">{trainingStats.average_score}%</div></CardContent>
              </Card>
            </div>

            {trainingCharts && (trainingCharts.by_department.length > 0 || trainingCharts.by_project.length > 0) && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {trainingCharts.by_department.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="uppercase tracking-tight text-base">Por Departamento</CardTitle>
                      <CardDescription>Cursos completados vs. en curso</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={trainingCharts.by_department}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" angle={-25} textAnchor="end" height={70} interval={0} fontSize={11} />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="completed" name="Completados" fill="#10B981" stackId="a" />
                          <Bar dataKey="in_progress" name="En curso" fill="#F59E0B" stackId="a" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}
                {trainingCharts.by_project.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="uppercase tracking-tight text-base">Por Proyecto</CardTitle>
                      <CardDescription>Cursos completados vs. en curso</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={trainingCharts.by_project}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" angle={-25} textAnchor="end" height={70} interval={0} fontSize={11} />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="completed" name="Completados" fill="#DC143C" stackId="a" />
                          <Bar dataKey="in_progress" name="En curso" fill="#F59E0B" stackId="a" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
